// Admin-only modal for configuring email notifications per incident category
// UPDATED: Added toast confirmation on save. Fixed flash on open by showing loader when form is not yet populated.
// UPDATED: Removed settings.length guard in useEffect so form always initializes (fixes infinite loader on empty DB).
// UPDATED: Simplified loader condition to isLoading only; content guard no longer checks form.length.

import { useState, useEffect } from 'react';
import { Users, PackageX, Wrench, HelpCircle, Mail, AlertCircle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';
import { useIncidentEmailSettings } from '../../hooks/useIncidentEmailSettings';
import { useToast } from '../../hooks/useToast';
import { INCIDENT_CATEGORIES } from '../../lib/incidents';
import type { Incident } from '../../lib/incidents';
import type { IncidentEmailSettingUpdate } from '../../lib/incidentSettings';
import { CategoryEmailSection, isValidEmail, type EmailEntry } from './CategoryEmailSection';

interface CategoryFormState {
  category: Incident['category'];
  is_enabled: boolean;
  emails: EmailEntry[];
}

interface IncidentEmailSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORY_ICONS = {
  client: <Users size={18} className="text-blue-600" />,
  stock: <PackageX size={18} className="text-orange-600" />,
  equipment: <Wrench size={18} className="text-red-600" />,
  other: <HelpCircle size={18} className="text-gray-600" />,
} as const;

const CATEGORY_BADGE_COLORS = {
  client: 'bg-blue-50',
  stock: 'bg-orange-50',
  equipment: 'bg-red-50',
  other: 'bg-gray-100',
} as const;

function buildInitialForm(settings: { category: string; is_enabled: boolean; email_addresses: string[] }[]): CategoryFormState[] {
  return INCIDENT_CATEGORIES.map(cat => {
    const saved = settings.find(s => s.category === cat.value);
    const addresses = saved?.email_addresses ?? [];
    return {
      category: cat.value,
      is_enabled: saved?.is_enabled ?? false,
      emails: addresses.length > 0
        ? addresses.map(v => ({ value: v, blurred: false }))
        : [],
    };
  });
}

export function IncidentEmailSettingsModal({ isOpen, onClose }: IncidentEmailSettingsModalProps) {
  const { settings, isLoading, isSaving, error, saveError, save } = useIncidentEmailSettings(isOpen);
  const { showToast } = useToast();
  const [form, setForm] = useState<CategoryFormState[]>([]);
  const [formSubmittedOnce, setFormSubmittedOnce] = useState(false);

  useEffect(() => {
    setForm(buildInitialForm(settings));
    setFormSubmittedOnce(false);
  }, [settings]);

  useEffect(() => {
    if (!isOpen) {
      setFormSubmittedOnce(false);
    }
  }, [isOpen]);

  const updateCategory = (catIndex: number, updater: (state: CategoryFormState) => CategoryFormState) => {
    setForm(prev => prev.map((item, i) => i === catIndex ? updater(item) : item));
  };

  const handleToggle = (catIndex: number) => {
    updateCategory(catIndex, state => {
      const next = { ...state, is_enabled: !state.is_enabled };
      if (next.is_enabled && next.emails.length === 0) {
        next.emails = [{ value: '', blurred: false }];
      }
      return next;
    });
  };

  const handleEmailChange = (catIndex: number, emailIndex: number, value: string) => {
    updateCategory(catIndex, state => ({
      ...state,
      emails: state.emails.map((e, i) => i === emailIndex ? { ...e, value } : e),
    }));
  };

  const handleEmailBlur = (catIndex: number, emailIndex: number) => {
    updateCategory(catIndex, state => ({
      ...state,
      emails: state.emails.map((e, i) => i === emailIndex ? { ...e, blurred: true } : e),
    }));
  };

  const handleEmailRemove = (catIndex: number, emailIndex: number) => {
    updateCategory(catIndex, state => ({
      ...state,
      emails: state.emails.filter((_, i) => i !== emailIndex),
    }));
  };

  const handleEmailAdd = (catIndex: number) => {
    updateCategory(catIndex, state => ({
      ...state,
      emails: [...state.emails, { value: '', blurred: false }],
    }));
  };

  const hasValidationErrors = form.some(cat => {
    if (!cat.is_enabled) return false;
    if (cat.emails.length === 0) return true;
    return cat.emails.some(e => e.value.trim() === '' || !isValidEmail(e.value));
  });

  const handleSave = async () => {
    setFormSubmittedOnce(true);

    if (hasValidationErrors) return;

    const updates: IncidentEmailSettingUpdate[] = form.map(cat => ({
      category: cat.category,
      is_enabled: cat.is_enabled,
      email_addresses: cat.emails.map(e => e.value.trim()).filter(Boolean),
    }));

    const ok = await save(updates);
    if (ok) {
      showToast('Paramètres de notification enregistrés', 'success');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Notifications par email" size="lg">
      <div className="flex flex-col max-h-[75vh]">
        <div className="flex items-center gap-2 px-6 pt-2 pb-4 border-b border-gray-100">
          <Mail size={16} className="text-gray-400" />
          <p className="text-sm text-gray-500">
            Configurez les destinataires pour chaque thématique d'incident.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-[280px]">
          {isLoading && (
            <div className="flex items-center justify-center min-h-[200px]">
              <BouncingDotsLoader size="lg" />
            </div>
          )}

          {!isLoading && error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {!isLoading && !error && (
            <div className="space-y-3">
              {form.map((cat, catIndex) => {
                const catInfo = INCIDENT_CATEGORIES.find(c => c.value === cat.category);
                return (
                  <CategoryEmailSection
                    key={cat.category}
                    icon={CATEGORY_ICONS[cat.category]}
                    label={catInfo?.label ?? cat.category}
                    badgeColor={CATEGORY_BADGE_COLORS[cat.category]}
                    isEnabled={cat.is_enabled}
                    emails={cat.emails}
                    submittedOnce={formSubmittedOnce}
                    onToggle={() => handleToggle(catIndex)}
                    onEmailChange={(emailIndex, value) => handleEmailChange(catIndex, emailIndex, value)}
                    onEmailBlur={emailIndex => handleEmailBlur(catIndex, emailIndex)}
                    onEmailRemove={emailIndex => handleEmailRemove(catIndex, emailIndex)}
                    onEmailAdd={() => handleEmailAdd(catIndex)}
                  />
                );
              })}
            </div>
          )}

          {saveError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mt-4">
              <AlertCircle size={16} />
              <span>{saveError}</span>
            </div>
          )}
        </div>

        <div className="shrink-0 flex gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
              !isSaving && !isLoading
                ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-sm hover:shadow-md'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Enregistrement...</span>
              </>
            ) : (
              'Enregistrer'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
